import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  RiDeleteBinFill,
  RiPencilLine,
  RiCloseLine,
  RiEdit2Line,
  RiLockPasswordLine,
  RiMailSendLine,
} from "react-icons/ri";
import { ContentLoader } from "../../Utility/Loader";
import NoDataFound from "../../Utility/NoDataFound/NoDataFound";
import ListPaginationBar from "../../Utility/Pagination/ListPaginationBar";
import { DEFAULT_LIST_PAGE_SIZE } from "../../Utility/Pagination/listPaginationConstants";
import UserFilter from "./UserFilter";
import Typography from "../../Utility/Typography";
import { useSorting, SortableHeader } from "../../Utility/Sorting";
import Button from "../../Utility/Button/Button";
import FloatingTooltip from "../../Utility/FloatingTooltip/FloatingTooltip";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import Avatar from "../../Utility/Avatar/Avatar";
import "./users.css";
import UserRateMappingAddModel from "../Billing/AddMappingModels/UserRateMappingModel";
import { addUserRateMapping } from "../../Redux/Slices/userRateMappingSlice";
import {
  deleteUser,
  fetchUsersData,
  updateUser,
  sendUserPasswordResetEmail,
  sendUserVerificationEmail,
} from "../../Redux/Thunks/usersThunk";
import DeleteConfirmModal from "../../Utility/DeleteConfirmModal/DeleteConfirmModal";
import {
  mapUserToTableRow,
  canAdminSendUserPasswordReset,
  canAdminSendUserVerificationEmail,
  getUserEmailForApi,
} from "./userMappers";
import { showSuccess, showError } from "../../utils/toast";
import { usePermissions } from "../../hooks/usePermissions";
import PermissionDeniedModal from "../../Utility/PermissionDeniedModal/PermissionDeniedModal";
import { PERMISSION_KEYS } from "../../permissions/permissionKeys";
import { APP_ROLE } from "../../permissions/roleKinds";

const UserList = ({
  usersData,
  loading,
  searchTerm,
  currentPage,
  setCurrentPage,
  limit = DEFAULT_LIST_PAGE_SIZE,
  onPageSizeChange,
  listingFilter,
  handleFilterChange,
  handleFilterReset,
  applyFilters,
  onRefetchUsers,
  roleList = [],
  teamOptions = [],
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [openMenuId, setOpenMenuId] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const menuRefs = useRef({});
  const [isRateModalOpen, setIsRateModalOpen] = useState(false);
  const [selectedUserForRate, setSelectedUserForRate] = useState(null);
  const [savingRoleUserId, setSavingRoleUserId] = useState(null);
  const [emailActionConfirm, setEmailActionConfirm] = useState(null);
  const [emailActionLoading, setEmailActionLoading] = useState(false);
  const [deniedPermissionKey, setDeniedPermissionKey] = useState(null);
  const { can, roleKind } = usePermissions();

  const canEditUser = can(PERMISSION_KEYS.USERS_EDIT);
  const canDeleteUser = can(PERMISSION_KEYS.USERS_DELETE);
  const isAdmin = roleKind === APP_ROLE.ADMIN;
  const showActionsColumn = canEditUser || canDeleteUser || isAdmin;
  const colCount = showActionsColumn ? 8 : 7;

  // State for inline editing
  const [editingField, setEditingField] = useState(null);
  const [localUsers, setLocalUsers] = useState([]);

  // Sync local users with prop data (including empty list)
  useEffect(() => {
    setLocalUsers(usersData?.list ?? []);
  }, [usersData?.list]);

  const roleOptions = roleList.map((r) => ({
    value: String(r.id),
    label: r.name ?? String(r.id),
  }));

  const getUserRoleId = (user) =>
    user?.roleId ?? (typeof user?.role === "object" ? user.role?.id : undefined) ?? null;

  const getUserRoleLabel = (user) =>
    (typeof user?.role === "object" ? user.role?.name : user?.role) ?? "";

  // Handle double click to start editing
  const handleDoubleClick = (userId, field) => {
    if (field === "role" && !canEditUser) {
      setDeniedPermissionKey(PERMISSION_KEYS.USERS_EDIT);
      return;
    }
    setEditingField({ userId, field });
  };

  // Handle inline edit (role change)
  const handleInlineEdit = async (user, field, value) => {
    if (field !== "role" || !user?.id || !value) return;
    if (!canEditUser) {
      setDeniedPermissionKey(PERMISSION_KEYS.USERS_EDIT);
      return;
    }
    const previousRoleLabel = getUserRoleLabel(user);
    const selectedRole = roleList.find((r) => String(r.id) === String(value));
    const nextRoleLabel = selectedRole?.name ?? previousRoleLabel ?? "";

    setSavingRoleUserId(user.id);
    setLocalUsers((prevUsers) =>
      prevUsers.map((u) =>
        u.id === user.id ? { ...u, roleId: String(value), role: nextRoleLabel } : u
      )
    );
    setEditingField(null);

    try {
      await dispatch(
        updateUser({
          id: user.id,
          userData: { roleId: String(value) },
        })
      ).unwrap();
      showSuccess("User role updated successfully");

      if (onRefetchUsers) onRefetchUsers();
      else dispatch(fetchUsersData({ page: 1, limit: limit || DEFAULT_LIST_PAGE_SIZE }));
    } catch (error) {
      // Revert optimistic update on failure
      setLocalUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === user.id ? { ...u, roleId: getUserRoleId(user), role: previousRoleLabel } : u
        )
      );
      showError(error?.message || "Failed to update user role");
    } finally {
      setSavingRoleUserId(null);
    }
  };

  // List from API (already filtered by search & role on server)
  const meta = usersData?.meta ?? null;
  const totalCount = meta?.totalCount ?? meta?.total ?? localUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / (limit || DEFAULT_LIST_PAGE_SIZE)));

  // Optional client-side sort on current page
  const { sortedData, sortKey, sortDirection, handleSort } = useSorting(localUsers, {
    defaultSortKey: null,
    defaultSortDirection: "asc",
  });

  const paginatedUsers = sortedData;

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(menuRefs.current).forEach((id) => {
        if (menuRefs.current[id] && !menuRefs.current[id].contains(event.target)) {
          setOpenMenuId(null);
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleMenu = (userId) => {
    setOpenMenuId(openMenuId === userId ? null : userId);
  };

  const handleDeleteClick = (user) => {
    if (!canDeleteUser) {
      setDeniedPermissionKey(PERMISSION_KEYS.USERS_DELETE);
      return;
    }
    setOpenMenuId(null);
    setUserToDelete(user);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete?.id) return;
    setDeleteLoading(true);
    try {
      await dispatch(deleteUser(userToDelete.id)).unwrap();
      showSuccess("User deleted successfully");
      if (onRefetchUsers) onRefetchUsers();
      else dispatch(fetchUsersData({ page: 1, limit: limit || DEFAULT_LIST_PAGE_SIZE }));
      setUserToDelete(null);
    } catch (err) {
      const msg =
        typeof err === "string"
          ? err
          : err?.message && String(err.message).trim() !== ""
            ? String(err.message)
            : "Failed to delete user";
      showError(msg);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleOpenRateMapping = (user) => {
    if (!can(PERMISSION_KEYS.BILLING_VIEW)) {
      setDeniedPermissionKey(PERMISSION_KEYS.BILLING_VIEW);
      return;
    }
    setSelectedUserForRate(user);
    setIsRateModalOpen(true);
  };

  const handleOpenVerificationEmailConfirm = (user) => {
    if (!isAdmin || !canAdminSendUserVerificationEmail(user)) return;
    setEmailActionConfirm({ type: "verification", user });
  };

  const handleOpenResetPasswordConfirm = (user) => {
    if (!isAdmin || !canAdminSendUserPasswordReset(user)) return;
    const email = getUserEmailForApi(user);
    if (!email) {
      showError("Cannot send reset email: user has no email address.");
      return;
    }
    setEmailActionConfirm({ type: "reset", user });
  };

  const handleConfirmEmailAction = async () => {
    if (!emailActionConfirm?.user) return;
    const { type, user } = emailActionConfirm;
    setEmailActionLoading(true);
    try {
      if (type === "verification") {
        await dispatch(sendUserVerificationEmail(user.id)).unwrap();
        showSuccess("Verification email sent successfully.");
      } else {
        const email = getUserEmailForApi(user);
        await dispatch(sendUserPasswordResetEmail(email)).unwrap();
        showSuccess("Password reset email sent successfully.");
      }
      setEmailActionConfirm(null);
    } catch (err) {
      const defaultMsg =
        type === "verification"
          ? "Failed to send verification email."
          : "Failed to send password reset email.";
      showError(typeof err === "string" ? err : err?.message || defaultMsg);
    } finally {
      setEmailActionLoading(false);
    }
  };

  const emailActionUser = emailActionConfirm?.user;
  const emailActionRow = emailActionUser ? mapUserToTableRow(emailActionUser) : null;
  const emailActionType = emailActionConfirm?.type;

  const handleSaveUserRateMapping = ({ user, role, client, rate }) => {
    const effectiveUser = selectedUserForRate?.name || user || "Unknown User";
    const effectiveClient = client || "-";
    const newItem = {
      id: Date.now(),
      user: effectiveUser,
      role,
      client: effectiveClient,
      rate: Number(rate),
    };
    dispatch(addUserRateMapping(newItem));
    setIsRateModalOpen(false);
  };

  return (
    <div className="users-page-content">
      <div className="w-full ">
        <ContentLoader loading={loading} className="min-h-[calc(100vh-12rem)]">
          {/* Filter Section */}
          <div className="">
            <UserFilter
              listingFilter={listingFilter}
              handleFilterChange={handleFilterChange}
              handleFilterReset={handleFilterReset}
              applyFilters={applyFilters}
              roleNames={roleList.map((r) => r?.name).filter(Boolean)}
              teamOptions={teamOptions}
            />
          </div>

          {/* Table Card */}
          <div>
            <div className="w-full">
              <div className="relative flex flex-col min-w-0 bg-surface-primary rounded-md  shadow-sm transition-shadow">
                <div className="flex-1 p-2">
                  <div className="live-preview">
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse mb-0 clients-table ">
                        <thead className="bg-surface-secondary">
                          <tr>
                            <SortableHeader
                              className="content-cell"
                              columnKey="name"
                              label="Name"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                            />
                            <SortableHeader
                              className="content-cell"
                              columnKey="designation"
                              label="Designation"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                            />
                            <SortableHeader
                              className="content-cell"
                              columnKey="email"
                              label="Email"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                            />
                            <SortableHeader
                              className="content-cell"
                              columnKey="mobile"
                              label="Mobile"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                            />
                            <SortableHeader
                              className="content-cell"
                              columnKey="role"
                              label={
                                <Typography
                                  variant="span1"
                                  className="inline-flex items-center gap-1"
                                >
                                  Role
                                </Typography>
                              }
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                            />
                            <SortableHeader
                              className="content-cell"
                              columnKey="team"
                              label="Team"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                            />
                            <SortableHeader
                              className="content-cell"
                              columnKey="status"
                              label="Status"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                              sortable={false}
                            />
                            {showActionsColumn ? (
                              <SortableHeader
                                columnKey="action"
                                label="Action"
                                onSort={handleSort}
                                currentSortKey={sortKey}
                                sortDirection={sortDirection}
                                sortable={false}
                                className="action-col"
                              />
                            ) : null}
                          </tr>
                        </thead>
                        <tbody className="relative">
                          {loading ? (
                            <tr aria-hidden="true">
                              <td colSpan={colCount} className="min-h-[240px] h-60 p-0 border-0" />
                            </tr>
                          ) : (
                            <>
                              {paginatedUsers.length > 0 ? (
                                paginatedUsers.map((user) => {
                                  const row = mapUserToTableRow(user);
                                  const badgeClass =
                                    row.statusDisplay.badgeVariant === "success"
                                      ? "bg-badge-success"
                                      : row.statusDisplay.badgeVariant === "danger"
                                        ? "bg-badge-danger"
                                        : "bg-badge-secondary";
                                  return (
                                    <tr key={user.id}>
                                      <td className="text-start align-middle">
                                        <div className="flex items-center gap-2">
                                          <Avatar name={row.nameForAvatar} />
                                          <Typography variant="span1">{row.name}</Typography>
                                        </div>
                                      </td>
                                      <td className="text-start align-middle">
                                        <Typography variant="span1">{row.designation}</Typography>
                                      </td>
                                      <td className="text-start align-middle">
                                        <Typography variant="span1">{row.email}</Typography>
                                      </td>
                                      <td className="text-start align-middle">
                                        <Typography variant="span1">{row.mobile}</Typography>
                                      </td>
                                      {/* Role - Inline Editable */}
                                      <td
                                        className="text-start align-middle px-3 py-3"
                                        onDoubleClick={
                                          canEditUser
                                            ? (e) => {
                                                e.stopPropagation();
                                                handleDoubleClick(user.id, "role");
                                              }
                                            : undefined
                                        }
                                      >
                                        {editingField?.userId === user.id &&
                                        editingField?.field === "role" ? (
                                          <div className="dropdown-wrapper flex items-center gap-2">
                                            <Dropdown
                                              name="role"
                                              value={getUserRoleId(user) ?? ""}
                                              onChange={(e) => {
                                                if (e.target.value) {
                                                  handleInlineEdit(user, "role", e.target.value);
                                                }
                                              }}
                                              options={roleOptions}
                                              placeholder="Select role"
                                              noWrapper={true}
                                              className="w-[130px]"
                                            />
                                            <Button
                                              type="button"
                                              variant="icon"
                                              size="vsm"
                                              aria-label="Cancel"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setEditingField(null);
                                              }}
                                              className="p-1 rounded hover:bg-gray-200 text-gray-600 hover:text-gray-800"
                                            >
                                              <RiCloseLine className="text-sm" />
                                            </Button>
                                          </div>
                                        ) : (
                                          <div
                                            className={`inline-flex items-center gap-1.5 group ${canEditUser ? "cursor-pointer" : ""}`}
                                          >
                                            <Typography
                                              variant="span1"
                                              className={`badge rounded-full px-3 py-1 text-white text-xs font-bold bg-badge-info ${canEditUser ? "group-hover:ring-2 group-hover:ring-blue-300 group-hover:ring-offset-1 transition-all" : ""}`}
                                              title={
                                                canEditUser
                                                  ? "Double-click to edit role"
                                                  : undefined
                                              }
                                            >
                                              {savingRoleUserId === user.id
                                                ? "Saving..."
                                                : row.role}
                                            </Typography>
                                            {canEditUser ? (
                                              <RiEdit2Line
                                                className="text-gray-400 opacity-40 group-hover:opacity-100 group-hover:text-blue-500 transition-all text-sm"
                                                title="Double-click to edit"
                                              />
                                            ) : null}
                                          </div>
                                        )}
                                      </td>
                                      <td className="text-start align-middle">
                                        <Typography variant="span1">{row.teamDisplay}</Typography>
                                      </td>
                                      <td className="text-start align-middle">
                                        <Typography
                                          variant="span1"
                                          className={`badge rounded-full px-3 py-1 text-white text-xs font-bold ${badgeClass}`}
                                        >
                                          {row.statusDisplay.text}
                                        </Typography>
                                      </td>
                                      {showActionsColumn ? (
                                        <td className="text-start align-middle">
                                          <div className="flex gap-0.5 items-center justify-end gap-2">
                                            {canEditUser ? (
                                              <FloatingTooltip label="Edit">
                                                <Button
                                                  onClick={() =>
                                                    navigate(`/users/edit/${user.id}`, {
                                                      state: {
                                                        previousPath:
                                                          location.pathname + location.search || "",
                                                      },
                                                    })
                                                  }
                                                  aria-label="Edit user"
                                                  size="sm"
                                                  className="
                                        btn-warning-new
                                        !w-7 !h-7  
                                        !p-0 
                                        !font-normal 
                                        !leading-none
                                        flex items-center justify-center
                                        border-0
                                      "
                                                >
                                                  <RiPencilLine className="text-white text-[12px]" />
                                                </Button>
                                              </FloatingTooltip>
                                            ) : null}

                                            {canDeleteUser ? (
                                              <FloatingTooltip label="Delete">
                                                <Button
                                                  onClick={() => handleDeleteClick(user)}
                                                  aria-label="Delete user"
                                                  size="sm"
                                                  className="btn-danger-new !w-7 !h-7  !p-0 flex items-center justify-center hover:opacity-90 border-0"
                                                >
                                                  <RiDeleteBinFill className="text-white text-[12px]" />
                                                </Button>
                                              </FloatingTooltip>
                                            ) : null}

                                            {isAdmin && canAdminSendUserVerificationEmail(user) ? (
                                              <FloatingTooltip label="Send verification email">
                                                <Button
                                                  onClick={() =>
                                                    handleOpenVerificationEmailConfirm(user)
                                                  }
                                                  aria-label="Send verification email"
                                                  size="sm"
                                                  className="btn-success-new !w-7 !h-7 !p-0 flex items-center justify-center border-0"
                                                >
                                                  <RiMailSendLine className="text-white text-[12px]" />
                                                </Button>
                                              </FloatingTooltip>
                                            ) : null}

                                            {isAdmin && canAdminSendUserPasswordReset(user) ? (
                                              <FloatingTooltip label="Reset password">
                                                <Button
                                                  onClick={() =>
                                                    handleOpenResetPasswordConfirm(user)
                                                  }
                                                  aria-label="Send reset password email"
                                                  size="sm"
                                                  className="btn-info-new !w-7 !h-7 !p-0 flex items-center justify-center border-0"
                                                >
                                                  <RiLockPasswordLine className="text-white text-[12px]" />
                                                </Button>
                                              </FloatingTooltip>
                                            ) : null}
                                          </div>
                                        </td>
                                      ) : null}
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={colCount} className="text-center">
                                    <NoDataFound
                                      message={
                                        searchTerm
                                          ? "No users found matching your search"
                                          : "No users available"
                                      }
                                    />
                                  </td>
                                </tr>
                              )}
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center mt-2 text-center sm:text-start"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {(usersData?.list ?? []).length > 0 && (
            <ListPaginationBar
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={limit}
              onPageChange={handlePageChange}
              onPageSizeChange={onPageSizeChange}
            />
          )}
        </ContentLoader>
      </div>

      {/* User Rate Mapping Modal */}
      {isRateModalOpen && selectedUserForRate && (
        <UserRateMappingAddModel
          onClose={() => setIsRateModalOpen(false)}
          onSave={handleSaveUserRateMapping}
          userOptions={[selectedUserForRate.name]}
          roleOptions={selectedUserForRate.role ? [selectedUserForRate.role] : []}
          clientOptions={["-"]}
        />
      )}

      <DeleteConfirmModal
        isOpen={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete user"
        itemName={userToDelete ? mapUserToTableRow(userToDelete).name : ""}
        loading={deleteLoading}
      />
      <DeleteConfirmModal
        isOpen={!!emailActionConfirm}
        onClose={() => !emailActionLoading && setEmailActionConfirm(null)}
        onConfirm={handleConfirmEmailAction}
        title={
          emailActionType === "verification"
            ? "Send verification email"
            : "Send reset password email"
        }
        message={
          emailActionType === "verification" ? (
            <>
              Are you sure you want to send a verification email to{" "}
              <strong>{emailActionRow?.name ?? "this user"}</strong>
              {emailActionRow?.email && emailActionRow.email !== "N/A" ? (
                <> ({emailActionRow.email})</>
              ) : null}
              ?
            </>
          ) : (
            <>
              Are you sure you want to send a password reset email to{" "}
              <strong>{emailActionRow?.email ?? "this user"}</strong>?
            </>
          )
        }
        loading={emailActionLoading}
        confirmLabel="Send"
        confirmVariant={emailActionType === "verification" ? "success" : "primary"}
        loadingLabel="Sending…"
        showDeleteIcon={false}
      />
      <PermissionDeniedModal
        isOpen={deniedPermissionKey != null}
        onClose={() => setDeniedPermissionKey(null)}
        permissionKey={deniedPermissionKey}
      />
    </div>
  );
};

export default UserList;
